import { InputTextTypes, InputTextCoreProps } from "./types"
import React, { useState, useMemo } from "react"
import { TextInput } from "~/ui/primitives"
import { ErrorText } from "~widgets/error-text"
import { useTheme } from '~/ui/styled'
import useValidateInput from "~hooks/useValidateInput"
import { InputTextInner } from "./input-text"
import { InputTextBorderInner } from "./input-text-border"
import { InputTextBottomLineInner } from "./input-text-bottom-line"
import { InputTextFloatLabelInner } from "./input-text-float-label"
import styled from '~/ui/styled'

const InputGroup = styled.View`
  width: 100%;
  padding-bottom: ${({ theme }) => theme.space.sm};
  position: relative;
`

const TextButtomArea = styled.View`
  position: absolute;
  flex-direction: row;
  justify-content: space-between;
  bottom: 4px;
  right: 0px;
`

const TextLengthCounter = styled.Text`
  color: ${({ theme }) => theme.colors.secondaryText};
`

const InputTextCoreUnrefed: React.ForwardRefRenderFunction<TextInput, InputTextCoreProps> = (
  { type, label, validRule, rightAccessory, ...rest },
  ref,
) => {
  const theme = useTheme()
  const { error, validate } = useValidateInput(validRule)
  const [isFocus, setIsFocus] = useState(false)

  const Components = {
    [InputTextTypes.INPUT_TEXT]: InputTextInner,
    [InputTextTypes.INPUT_TEXT_BORDER]: InputTextBorderInner,
    [InputTextTypes.INPUT_TEXT_BOTTOM_LINE]: InputTextBottomLineInner,
    [InputTextTypes.INPUT_TEXT_FLOAT_LABEL]: InputTextFloatLabelInner,
  }

  const MatchComponent = useMemo(() => Components[type], [type])

  const overidedOnFocus = (e) => {
    rest?.onFocus?.(e)
    setIsFocus(true)
  }

  const overidedOnBlur = (e) => {
    rest?.onBlur?.(e)
    setIsFocus(false)
  }

  const overidedOnChangeText = (value) => {
    rest?.onChangeText?.(value)
    validate(value)
  }

  if (validRule || rest.maxLength) {
    return (
      <InputGroup>
        <MatchComponent
          // text input props
          autoCapitalize="none"
          placeholderTextColor={theme.colors.inputPlaceholderColor}
          {...rest}
          onFocus={overidedOnFocus}
          onBlur={overidedOnBlur}
          onChangeText={overidedOnChangeText}
          ref={ref}
          // our props
          label={label}
          error={error}
          isFocus={isFocus}
          {...(type === InputTextTypes.INPUT_TEXT_FLOAT_LABEL ? { rightAccessory } : {})}
        />
        {error ? <ErrorText text={error[0]} /> : null}
        {rest.maxLength && (
          <TextButtomArea>
            <TextLengthCounter>
              {(typeof rest?.value === "string"
                ? rest.value.length
                : rest?.defaultValue?.length) || 0}
              /{rest.maxLength || 0}
            </TextLengthCounter>
          </TextButtomArea>
        )}
      </InputGroup>
    )
  } else {
    return (
      <InputGroup style={{ paddingBottom: 0 }}>
        <MatchComponent
          // text input props
          autoCapitalize="none"
          placeholderTextColor={theme.colors.inputPlaceholderColor}
          {...rest}
          onFocus={overidedOnFocus}
          onBlur={overidedOnBlur}
          onChangeText={overidedOnChangeText}
          ref={ref}
          // our props
          label={label}
          error={error}
          isFocus={isFocus}
          {...(type === InputTextTypes.INPUT_TEXT_FLOAT_LABEL ? { rightAccessory } : {})}
        />
      </InputGroup>
    )
  }
}

export const InputTextCore = React.forwardRef(InputTextCoreUnrefed)
